import { proxyService } from '../services/proxyService';

export type ProxyDiagnosticCheck = {
  id: string;
  label: string;
  status: 'pass' | 'fail' | 'warn' | 'info';
  detail: string;
};

export type ProxyDiagnosticReport = {
  healthy: boolean;
  summary: string;
  checks: ProxyDiagnosticCheck[];
  /** Plain-text reply suitable for AI Agent chat. */
  agentReply: string;
};

function isRemoteInsecurePage(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  const loopback =
    host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
  return !window.isSecureContext && !loopback;
}

/**
 * Run client-side checks for why the CRUD Expert desktop agent may be unavailable.
 * The agent runs on the user's machine — these checks must execute in the browser.
 */
export async function runProxyDiagnostics(): Promise<ProxyDiagnosticReport> {
  const checks: ProxyDiagnosticCheck[] = [];
  const configuredUrl = proxyService.getProxyUrl() || 'http://localhost:9000';

  checks.push({
    id: 'config_url',
    label: 'Configured proxy URL',
    status: 'info',
    detail: configuredUrl || 'http://localhost:9000',
  });

  if (isRemoteInsecurePage()) {
    checks.push({
      id: 'secure_context',
      label: 'Browser security context',
      status: 'fail',
      detail:
        'This site is HTTP (“Not Secure”). Chrome blocks localhost from public HTTP pages even when Local Network is allowed. Open the app over HTTPS, or use Safari temporarily.',
    });
  } else if (typeof window !== 'undefined' && window.isSecureContext) {
    checks.push({
      id: 'secure_context',
      label: 'Browser security context',
      status: 'pass',
      detail: 'Secure context (HTTPS or localhost) — Chromium can request loopback access.',
    });
  } else {
    checks.push({
      id: 'secure_context',
      label: 'Browser security context',
      status: 'info',
      detail: 'Running on a local/dev host.',
    });
  }

  let healthOk = false;
  let healthDetail = '';
  try {
    const result = await proxyService.testProxyConnection();
    healthOk = result.success === true;
    healthDetail = result.message || (healthOk ? 'Proxy health OK' : 'Health check failed');
    if (healthOk && result.details?.version) {
      healthDetail += ` (version ${result.details.version})`;
    }
  } catch (e: unknown) {
    healthDetail = e instanceof Error ? e.message : 'Health check threw an error';
  }

  checks.push({
    id: 'health',
    label: 'Proxy health (GET /health)',
    status: healthOk ? 'pass' : 'fail',
    detail: healthDetail,
  });

  if (!healthOk) {
    checks.push({
      id: 'service_running',
      label: 'Proxy process / service',
      status: 'fail',
      detail:
        'Nothing healthy answered on the proxy URL. Install and start the CRUD Expert desktop agent (crud-expert-proxy-setup.exe on Windows, curd-expert-proxy.dmg on macOS) or ensure Docker crud-proxy is running on port 9000.',
    });
    checks.push({
      id: 'port',
      label: 'Port 9000',
      status: 'warn',
      detail:
        'Default listen port is 9000. If another app owns it, stop that process or point Proxy Settings at the correct URL.',
    });
    checks.push({
      id: 'node_deps',
      label: 'Node.js / dependencies',
      status: 'info',
      detail:
        'Portable Mac/Windows proxy builds are self-contained — Node.js is NOT required. Only the older Node-based installer needs Node 18+.',
    });
    checks.push({
      id: 'manual_verify',
      label: 'Manual verification',
      status: 'info',
      detail: 'Open http://localhost:9000/health in this browser. You should see JSON with status "OK".',
    });
  } else {
    checks.push({
      id: 'service_running',
      label: 'Proxy process / service',
      status: 'pass',
      detail: 'The CRUD Expert desktop agent responded and is healthy.',
    });
  }

  const failed = checks.filter((c) => c.status === 'fail');
  const summary = healthOk
    ? 'CRUD Expert desktop agent is connected and healthy.'
    : `CRUD Expert desktop agent is not connected (${failed.length} issue${failed.length === 1 ? '' : 's'} found).`;

  const lines = [
    'Proxy connection diagnostics',
    '',
    summary,
    '',
    ...checks.map((c) => {
      const mark =
        c.status === 'pass' ? '✓' : c.status === 'fail' ? '✗' : c.status === 'warn' ? '!' : '•';
      return `${mark} ${c.label}: ${c.detail}`;
    }),
    '',
    healthOk
      ? 'You can continue using Collections and AI Agent request execution.'
      : 'Next: fix the failed checks above, then run “Diagnose proxy connection” again or click Test proxy connection.',
  ];

  return {
    healthy: healthOk,
    summary,
    checks,
    agentReply: lines.join('\n'),
  };
}
